import { Component, OnInit, SecurityContext, inject } from '@angular/core';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import {
  ContentItem,
  ContentType,
  PageType,
} from '@nimmsta/ng-commons-lib/models';
import { ContentsService } from '@nimmsta/ng-commons-lib/services/contents';
import { ErrorComponent } from '@nimmsta/ng-commons-lib/error';
import { HeaderBarComponent } from '@nimmsta/ng-commons-lib/header-bar';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import DOMPurify from 'dompurify';
import { InformationFolderComponent } from './information-folder.component';
import { MatIcon } from '@angular/material/icon';
import { TranslatePipe } from '@ngx-translate/core';

@Component({
  selector: 'app-information',
  templateUrl: './information-item.component.html',
  imports: [
    ErrorComponent,
    RouterLink,
    HeaderBarComponent,
    InformationFolderComponent,
    MatIcon,
    TranslatePipe,
  ],
})
export class InformationItemComponent implements OnInit {
  private contentService = inject(ContentsService);
  private route = inject(ActivatedRoute);
  private sanitizer = inject(DomSanitizer);

  id!: string;

  contentItem?: ContentItem;

  error?: HttpErrorResponse;

  cleanedContent?: SafeHtml;

  protected readonly ContentType = ContentType;

  protected readonly PageType = PageType;

  ngOnInit() {
    this.route.params.subscribe((params) => {
      this.id = params['id'];
      this.load();
    });
  }

  private load() {
    this.contentService.getForView(this.id).subscribe({
      next: (contentItem) => {
        this.contentItem = contentItem;
        this.cleanContent(contentItem.content);
        this.cleanAndAppendStyles(contentItem.content);
      },
      error: (error) => {
        this.error = error;
      },
    });
  }

  /**
   * Cleans the content by sanitizing it and removing unwanted tags and attributes among others.
   * <p>
   * Specifically allows iframes. Styles are removed because they are handled separately.
   * @param content The content to clean.
   */
  private cleanContent(content?: string): void {
    if (content) {
      const clean = DOMPurify.sanitize(content, {
        ADD_TAGS: ['iframe', 'embed'],
        ADD_ATTR: ['type', 'width', 'height', 'src', 'data-file-key'],
        FORBID_TAGS: ['style'],
        // allow http(s) + data:
        ALLOWED_URI_REGEXP: /^(?:(?:https?|mailto|ftp|tel|file|sms|data):|[^a-z]|[a-z+.-]+(?:[^a-z+.\-:]|$))/i,
      });
      this.cleanedContent = this.sanitizer.bypassSecurityTrustHtml(clean);
    }
  }

  /**
   * Cleans the styles from the content and appends them to the document head.
   * <p>
   * We do this separately to wrap the styles in a container with the content ID to avoid conflicts with other styles.
   * @param content The content from which to extract styles.
   */
  private cleanAndAppendStyles(content?: string): void {
    if (!content) {
      return;
    }
    const styles = Array.from(content.matchAll(/<style[^>]*>([\s\S]*?)<\/style>/gi)).map((match) => match[1]);
    if (styles.length === 0) {
      return;
    }
    const sanitizedStyles = this.sanitizer.sanitize(
      SecurityContext.STYLE,
      `#content-container-${this.id} {\n${styles.join('\n')}\n}`,
    );
    if (!sanitizedStyles) {
      return;
    }
    const style = document.createElement('style');
    style.textContent = sanitizedStyles;
    document.head.appendChild(style);
  }
}
